import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Dialog, DialogContent,
  IconButton, styled, Grid, FormControl, Select, MenuItem
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Close as CloseIcon, Check as CheckIcon } from '@mui/icons-material';
import AddressService from './addressService';
import AlertService from "../../components/Alerts/alerts";
import { useTranslation } from 'react-i18next';
import AddressPickerPopup from '../../components/AddressPicker/AddressPickerPopup';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    width: '100%',
    maxWidth: '600px',
    margin: 0,
  },
}));

const Header = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px',
}));

const AddressIcon = styled(LocationOnIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginRight: '8px',
}));

const StyledTextField = styled(TextField)(() => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      border: 'none',
    },
  },
  backgroundColor: '#F0F5FB',
  '& .MuiInputLabel-root': {
    fontSize: '16px',
    color: '#757575',
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px',
    fontSize: '13px',
  },
  marginBottom: 2,
}));

const ActionButton = styled(Button)(() => ({
  width: '30%',
  borderRadius: '10px',
  backgroundColor: '#3488A4',
  padding: '10px 20px',
  textTransform: 'none',
  fontWeight: 'bold',
  boxShadow: 'none',
}));

const CancelButton = styled(Button)(() => ({
  width: '30%',
  borderRadius: '10px',
  backgroundColor: '#E7E7E7',
  padding: '10px 20px',
  border: '1px solid #E7E7E7',
  textTransform: 'none',
  fontWeight: 'bold',
  color: '#16294E',
  boxShadow: 'none',
}));

const AddressPopup = ({ open, onClose, onAddNew }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    location: '',
    block: '',
    unit: '',
    label: '',
    latitude: '',
    longitude: '',
    address_url: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  });
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState({});
  const [isGoogleAddressSelected, setIsGoogleAddressSelected] = useState(false);
  const [errors, setErrors] = useState({});

  const handleOpenPopup = () => setPopupOpen(true);
  const handleClosePopup = () => setPopupOpen(false);
  const handleSelectAddress = (address) => {
    //iph todo: 47
    //console.log("address", address);
    setSelectedAddress(address);
    setIsGoogleAddressSelected(true);
    setErrors({}); // Clear all errors when Google address is selected
    setFormData({
      location: address.location,
      block: address.block,
      unit: address.unit,
      label: address.label,
      latitude: address.latitude,
      longitude: address.longitude,
      address_url: `https://www.google.com/maps?q=${address.latitude},${address.longitude}`,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country,
    });
  };
  const [storeduser] = useState(() => JSON.parse(localStorage.getItem('user')));

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));

    // Clear specific field error when user starts typing (only if not Google address)
    if (!isGoogleAddressSelected && errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // If user manually changes any field, mark as not Google selected
    if (isGoogleAddressSelected) {
      setIsGoogleAddressSelected(false);
    }
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // If Google address is selected, skip validation for auto-filled fields
    if (!isGoogleAddressSelected) {
      // if (!formData.location || !formData.location.trim()) {
      //   newErrors.location = t('Please select an address from Google');
      // }
      if (!formData.block || !formData.block.trim()) {
        newErrors.block = t('Block or Street name is required');
      }
      if (!formData.unit || !formData.unit.trim()) {
        newErrors.unit = t('Unit/House number is required');
      }
      if (!formData.city || !formData.city.trim()) {
        newErrors.city = t('City is required');
      }
      if (!formData.state || !formData.state.trim()) {
        newErrors.state = t('State/Province is required');
      }
      if (!formData.postalCode || !formData.postalCode.trim()) {
        newErrors.postalCode = t('Postal Code is required');
      }
      if (!formData.country || !formData.country.trim()) {
        newErrors.country = t('Country is required');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBack = () => {
    onClose();
    setFormData({
      location: '',
      block: '',
      unit: '',
      label: '',
      latitude: '',
      longitude: '',
      address_url: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
    });
    setErrors({});
    setIsGoogleAddressSelected(false);
    setSelectedAddress({});
  };

  const handleAddAddress = async () => {
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    try {
      const response = await AddressService.addAddress({
        user_id: storeduser._id,
        ...formData
      });

      if (response.data.status) {
        AlertService.swalSuccessAlert(t("Address added successfully."));
        onAddNew(response.data.address);
        handleBack();
      } else {
        AlertService.swalErrorAlert(response.data.message?.[0]?.message || t("Error adding address."));
      }
    } catch (error) {
      AlertService.swalErrorAlert(t("Error adding address. Please try again."));
    }
  };

  const malaysiaStates = [
    "Johor",
    "Kedah",
    "Kelantan",
    "Melaka",
    "Negeri Sembilan",
    "Pahang",
    "Perak",
    "Perlis",
    "Penang",
    "Sabah",
    "Sarawak",
    "Selangor",
    "Terengganu",
    "Kuala Lumpur",
    "Labuan",
    "Putrajaya"
  ];

  return (
    <StyledDialog open={open} onClose={onClose} fullWidth>
      <Header>
        <IconButton onClick={handleBack}>
          <ArrowBackIcon />
        </IconButton>
        <Box display="flex" alignItems="center">
          <AddressIcon />
          <Typography variant="subtitle1">Address</Typography>
        </Box>
      </Header>
      <DialogContent sx={{ padding: 0 }}>

        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }} variant="h6">
            {t('Address')}
          </Typography>
          {/* <StyledTextField
            label="Selected Address"
            value={selectedAddress.location || ''}
            fullWidth
            onClick={handleOpenPopup}
            readOnly
            error={!!errors.location}
            helperText={errors.location}
          /> */}
          <AddressPickerPopup
            open={popupOpen}
            onClose={handleClosePopup}
            onSelect={handleSelectAddress}
          />

          <Grid container spacing={2}>
            {[
              { label: 'Block or Street name', name: 'block' },
              { label: 'Unit/House no', name: 'unit' },
              { label: 'City', name: 'city' },
              { label: 'State/Province', name: 'state' },
              { label: 'Postal Code', name: 'postalCode' },
              { label: 'Country', name: 'country' }
            ].map((field, index) => (
              <Grid item xs={6} key={field.name}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }} variant="h6">
                  {t(field.label)}
                </Typography>

                {field.name === 'state' ? (
                  <StyledTextField
                    select
                    fullWidth
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleInputChange}
                    error={!!errors[field.name]}
                    helperText={errors[field.name]}
                  >
                    <MenuItem value="">
                      {t('Select State')}
                    </MenuItem>
                    {malaysiaStates.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </StyledTextField>
                ) : (
                  <StyledTextField
                    fullWidth
                    placeholder={field.label}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleInputChange}
                    error={!!errors[field.name]}
                    helperText={errors[field.name]}
                  />
                )}
              </Grid>
            ))}
          </Grid>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <CancelButton onClick={handleBack} startIcon={<CloseIcon />}>
              {t("Cancel")}
            </CancelButton>
            <ActionButton
              variant="contained"
              onClick={handleAddAddress}
              startIcon={<CheckIcon />}
            >
              {t("Add")}
            </ActionButton>
          </Box>
        </Box>
      </DialogContent>
      <style>{`
        .pac-container {
          z-index: 9999 !important;
          position: fixed !important;
        }
      `}</style>
    </StyledDialog>
  );
};

export default AddressPopup;